<script>
// `mix` is declared here rather than in setup because vue-meta evaluates page() through a watcher
// that cannot see <script setup> bindings, and the mix is fetched async so page() cannot re-derive
// it from $route or $store. Setup writes it once the fetch lands.
export default {
    data() {
        return {
            mix: {
                id: 0,
                type: 'mix',
                genre: [],
                artist: {
                    id: 0,
                    type: 'artist',
                },
                'track_list': Array.from(Array(7), () => ({})),
            },
        }
    },

    page() {
        const hasData = this.mix.show && this.mix.artist
        const title = hasData
            ? `${this.mix.artist.title} - ${this.mix.show.title}`
            : 'Mix'
        const genre = this.mix.genre && this.mix.genre.length ? this.mix.genre[0] : ''
        const description = hasData
            ? `A mix by ${this.mix.artist.title} on ${this.mix.show.title}${genre ? ' • ' + genre : ''}`
            : 'Listen on FRISKY'
        const image = (this.mix && this.mix.album_art && this.mix.album_art.url)
            || (this.mix && this.mix.show && this.mix.show.album_art && this.mix.show.album_art.url)
            || `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'music.song' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import DataLayer from '@services/data-layer/index'
import Layout from '@layouts/main-layout'
import MixMetaTile from '@components/mix-meta-tile'
import TracklistBlock from '@components/tracklist-block'
import MixGridBlock from '@components/mix-grid-block'

const instance = getCurrentInstance()
const route = useRoute()
const router = useRouter()
const store = useStore()

// Setup's own copy: a binding called `mix` would shadow the options data property the template
// and page() rely on.
const loadedMixId = ref(0)
const relatedMixes = ref([])

const playback = computed(() => store.state.playback)

const status = computed(() => playback.value?.status)

function handleUrlPlayParam() {
    if (
        status.value === 'playing' &&
        playback.value?.resource?.id === loadedMixId.value &&
        playback.value?.resource?.type === 'mix'
    ) {
        router.replace({ query: { ...route.query, play: 'true' } })
    } else {
        const { play, ...rest } = route.query
        if (JSON.stringify(rest) === JSON.stringify(route.query)) return
        router.replace({ query: rest })
    }
}

watch(status, handleUrlPlayParam)

onMounted(() => {
    Promise.all([
        DataLayer.mixes.getById(route.params.id),
        DataLayer.mixes.getRelatedByMix(route.params.id),
    ]).then(([mixModel, related]) => {
        mixModel.body.type = 'mix'
        loadedMixId.value = mixModel.body.id
        // page() reads this off the instance; `$data` is initialised by the time onMounted runs.
        instance.proxy.mix = mixModel.body
        relatedMixes.value = related.body
        handleUrlPlayParam()
    })
})
</script>

<template>
    <Layout>
        <BaseContent>
            <div class="container">
                <BaseSection padded>
                    <h6>Mix</h6>
                    <div class="content">
                        <div class="details">
                            <MixMetaTile :mix="mix" />
                        </div>
                        <div>
                            <h6 class="subtitle">Tracklist</h6>
                            <TracklistBlock :tracklist="mix['track_list']" />
                        </div>
                    </div>
                </BaseSection>
            </div>
            <BaseSection
                v-if="relatedMixes && relatedMixes.length"
                padded
                title="Related Mixes"
            >
                <MixGridBlock :mixes="relatedMixes" />
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.container {
    margin-top: $s-h;
}

.content {
    @include widescreen {
        display: grid;
        grid-template-columns: 2fr 5fr;
        gap: $s-xxxl;
    }
}

.details {
    max-width: 300px;
    margin: 0 auto;

    @include widescreen {
        margin: unset;
    }
}

.subtitle {
    margin-top: 0;
    margin-bottom: $s-m;
}
</style>
